import React from 'react';
import { baseConfig } from '../../../config/algolia';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { Configure, Index, InstantSearch } from 'react-instantsearch';
import { Wings } from '@jbhi-fi/components/src/Wings/Wings';
import { WingsTransformer } from '@jbhi-fi/components/src/Contentful/transformers/WingsTransformer';
import CustomInfiniteHits from './CustomInfiniteHits';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { ContentfulMetafieldCollection } from '@jbhi-fi/apis/src/contentful/types';
import { CollectionHeaderNav } from '@jbhi-fi/components/src/CollectionHeaderNav/CollectionHeaderNav';
import { CollectionHeader } from '@jbhi-fi/components/src/CollectionHeader/CollectionHeader';
import { styles } from './CollectionCuratedContainer.css';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { GoToLink } from '@jbhi-fi/components/src/GoToLink/GoToLink';
import { HeroBannerCarousel } from '@jbhi-fi/components/src/HeroBannerCarousel/HeroBannerCarousel';
import { CarouselBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselBannerTransformer';
import { useContentfulAds } from '@jbhi-fi/components/src/hooks/useContentfulAds';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { useMediaQuery } from '@jbhi-fi/lanyard-ui/utils/useMediaQuery';
import { InsightsMiddleware } from '../../../utils/algolia/InsightsMiddleware';
import { SearchContentContainer } from '../../../components/searchcontent/SearchContentContainer';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';
import { searchClientWithProxy } from '../../../utils/algolia/createInstantSearchProxy';

const MAX_CAROUSEL_BANNERS = 8;

const { product_families_index = '' } = baseConfig;

/**
 * Initialise the algolia search client and the index
 */
const algoliaSearchClient = searchClientWithProxy();
const indexName = product_families_index;

const CollectionEnhancedContainer = (collection: ContentfulMetafieldCollection) => {
  const shouldShowNav = !!collection?.browse?.length;
  const { contentfulThemeApiClient, contentfulClient } = usePublicApiClient();

  const { banners, wings, isLoading } = useContentfulAds({
    queryOptions: { handle: collection.handle },
    contentfulThemeApiClient,
    contentfulClient,
  });
  const shouldShowWings = useMediaQuery('(min-width: 1200px)');

  return (
    <InstantSearch searchClient={algoliaSearchClient} indexName={indexName}>
      <InsightsMiddleware />
      {isLoading || banners?.length ? (
        <HeroBannerCarousel
          {...CarouselBannerTransformer.toHeroBannerCarouselProps(banners.slice(0, MAX_CAROUSEL_BANNERS), {
            variant: 'collection',
          })}
          isLoading={isLoading}
          variant="collection"
        />
      ) : null}
      {shouldShowWings && wings ? <Wings {...WingsTransformer.toWingsProps(wings)} /> : null}
      <div className={styles.collectionHeading}>
        <CollectionHeader
          heading={collection.title}
          subText={collection.seoTextA}
          navigation={shouldShowNav ? <CollectionHeaderNav metafieldCollection={collection} /> : undefined}
        />
        <SearchContentContainer term={collection.title} />
      </div>
      {collection.curated_collections?.map(
        (section: any) =>
          section.products.length > 0 && (
            <div key={section.url} className={styles.curatedCollection}>
              <div className={styles.curatedSectionTitleContainer}>
                <DomFont as="h2" className={styles.curatedSectionTitle}>
                  {section.title}
                </DomFont>
                <GoToLink href={section.url} label={section.urlTitle} className={styles.moreButton} />
              </div>
              <Index indexName={product_families_index} indexId={section.title}>
                <Configure
                  clickAnalytics
                  hitsPerPage={4}
                  filters={buildSearchFilters(
                    section.products.map((sku: string) => `sku:${sku} OR skus:${sku}`).join(' OR ')
                  )}
                  distinct
                  analyticsTags={getAnalyticsTags('discovery')}
                />
                <CustomInfiniteHits collection={collection} showLoadMore={false} curatedSection={section.products} />
              </Index>
            </div>
          )
      )}
      {collection.seoTextB ? (
        <div className={styles.seoTextB}>
          <div dangerouslySetInnerHTML={{ __html: collection.seoTextB }} />
        </div>
      ) : null}
      <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
    </InstantSearch>
  );
};
export default CollectionEnhancedContainer;
